/**
 * WebGL2 context creation, capabilities and loss / restore (W2-01a preview-gl; ARCHITECTURE.md §7.3).
 *
 * The context is created like Phira's window surface: no alpha channel in the drawing buffer (`alpha: false`),
 * straight colours (`premultipliedAlpha: false`), no depth / stencil. It is always created WITHOUT antialiasing:
 * Phira's "MSAA" player setting (default off) renders the frame into a multisampled renderbuffer instead
 * (renderTargets.ts), so switching the setting never needs a new context and the canvas stays the same element
 * (Theater re-parents it).
 *
 * Loss: `webglcontextlost` is cancelled (so the browser may restore the context) and reported; on
 * `webglcontextrestored` the owner re-creates every GPU object from its CPU-side caches (ImageBitmaps, text
 * rasters, compiled engine output). GPU objects are derived data everywhere in this renderer.
 */

import { glObject } from './glState';

/** Attributes of the preview context (ARCHITECTURE.md §7.3). */
export const PREVIEW_CONTEXT_ATTRIBUTES: Readonly<WebGLContextAttributes> = {
  alpha: false,
  premultipliedAlpha: false,
  antialias: false,
  depth: false,
  stencil: false,
  preserveDrawingBuffer: false,
  powerPreference: 'high-performance',
  failIfMajorPerformanceCaveat: false,
  desynchronized: false,
};

/** Samples of the MSAA render path (Phira's MSAA setting; clamped to the driver's MAX_SAMPLES). */
export const MSAA_SAMPLES = 4;

/** Minimal typing of EXT_disjoint_timer_query_webgl2 (not in the DOM lib). */
export interface TimerQueryExt {
  readonly TIME_ELAPSED_EXT: number;
  readonly GPU_DISJOINT_EXT: number;
}

/** What the renderer needs to know about the driver. */
export interface GlCaps {
  /** Largest texture side. */
  readonly maxTextureSize: number;
  /** Largest renderbuffer side. */
  readonly maxRenderbufferSize: number;
  /** Samples the MSAA path uses (0 = multisampling unavailable). */
  readonly msaaSamples: number;
  /** GPU timer queries (null when the browser does not expose them). */
  readonly timer: TimerQueryExt | null;
}

/** Creates the preview's WebGL2 context, or null when the browser has none for this canvas. */
export function createGlContext(canvas: HTMLCanvasElement): WebGL2RenderingContext | null {
  try {
    const gl = canvas.getContext('webgl2', PREVIEW_CONTEXT_ATTRIBUTES);
    return gl ?? null;
  } catch {
    return null;
  }
}

function intParam(gl: WebGL2RenderingContext, pname: number, fallback: number): number {
  const v: unknown = gl.getParameter(pname);
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : fallback;
}

/** Reads the capabilities (fallbacks: the WebGL2 minimums). */
export function readCaps(gl: WebGL2RenderingContext): GlCaps {
  const maxSamples = intParam(gl, gl.MAX_SAMPLES, 0);
  return {
    maxTextureSize: intParam(gl, gl.MAX_TEXTURE_SIZE, 2048),
    maxRenderbufferSize: intParam(gl, gl.MAX_RENDERBUFFER_SIZE, 2048),
    msaaSamples: Math.min(MSAA_SAMPLES, maxSamples),
    timer: timerExt(gl),
  };
}

function timerExt(gl: WebGL2RenderingContext): TimerQueryExt | null {
  try {
    return gl.getExtension('EXT_disjoint_timer_query_webgl2') as TimerQueryExt | null;
  } catch {
    return null;
  }
}

/** Listens for loss and restore of a canvas' context. */
export class ContextWatcher {
  private readonly canvas: HTMLCanvasElement;
  private lostFlag = false;
  private readonly onLost: () => void;
  private readonly onRestored: () => void;
  private readonly lostListener = (e: Event): void => {
    // Without preventDefault the browser never restores the context.
    e.preventDefault();
    if (this.lostFlag) return;
    this.lostFlag = true;
    this.onLost();
  };
  private readonly restoredListener = (): void => {
    if (!this.lostFlag) return;
    this.lostFlag = false;
    this.onRestored();
  };

  constructor(canvas: HTMLCanvasElement, onLost: () => void, onRestored: () => void) {
    this.canvas = canvas;
    this.onLost = onLost;
    this.onRestored = onRestored;
    canvas.addEventListener('webglcontextlost', this.lostListener);
    canvas.addEventListener('webglcontextrestored', this.restoredListener);
  }

  /** True between a loss and the matching restore. */
  get lost(): boolean {
    return this.lostFlag;
  }

  /** Marks the context lost without an event (a call found `isContextLost()` true). */
  markLost(): void {
    if (this.lostFlag) return;
    this.lostFlag = true;
    this.onLost();
  }

  dispose(): void {
    this.canvas.removeEventListener('webglcontextlost', this.lostListener);
    this.canvas.removeEventListener('webglcontextrestored', this.restoredListener);
  }
}

/**
 * GPU frame time through EXT_disjoint_timer_query_webgl2: one query per frame, results read back a few frames later
 * (never blocking). `latestMs` is the most recent finished measurement, or null when unavailable.
 */
export class GpuTimer {
  private readonly gl: WebGL2RenderingContext;
  private readonly ext: TimerQueryExt;
  private readonly pending: WebGLQuery[] = [];
  private active: WebGLQuery | null = null;
  latestMs: number | null = null;

  constructor(gl: WebGL2RenderingContext, ext: TimerQueryExt) {
    this.gl = gl;
    this.ext = ext;
  }

  begin(): void {
    if (this.active !== null || this.pending.length > 4) return;
    const q = glObject(this.gl.createQuery());
    if (q === null) return;
    this.gl.beginQuery(this.ext.TIME_ELAPSED_EXT, q);
    this.active = q;
  }

  end(): void {
    const q = this.active;
    if (q === null) return;
    this.gl.endQuery(this.ext.TIME_ELAPSED_EXT);
    this.pending.push(q);
    this.active = null;
    this.poll();
  }

  /**
   * Reads finished queries. Availability is checked first (answered from the browser's cache, never a stall); the
   * disjoint flag, a synchronous round trip to the GPU process, is read only when a result is there to use (UX-028).
   */
  private poll(): void {
    const gl = this.gl;
    let disjoint: unknown = null;
    while (this.pending.length > 0) {
      const q = this.pending[0];
      if (q === undefined) break;
      const ready: unknown = gl.getQueryParameter(q, gl.QUERY_RESULT_AVAILABLE);
      if (ready !== true) break;
      this.pending.shift();
      const ns: unknown = gl.getQueryParameter(q, gl.QUERY_RESULT);
      gl.deleteQuery(q);
      disjoint ??= gl.getParameter(this.ext.GPU_DISJOINT_EXT);
      if (disjoint !== true && typeof ns === 'number' && Number.isFinite(ns)) this.latestMs = ns / 1e6;
    }
  }

  dispose(): void {
    const gl = this.gl;
    if (this.active !== null) {
      gl.endQuery(this.ext.TIME_ELAPSED_EXT);
      gl.deleteQuery(this.active);
      this.active = null;
    }
    for (const q of this.pending) gl.deleteQuery(q);
    this.pending.length = 0;
  }
}
